import { Controller, useForm } from 'react-hook-form';
import { Box, Flex } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import * as Form from 'components/_core/form';
import OBBackButton from 'features/Onboarding/components/OBBackButton';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import OBStickyButton from 'features/Onboarding/components/OBStickyButton/OBStickyButton';

import Header from '../../../../components/Header';
import { ExtendedBusinessForm } from '../../types';
import locales from './locales';

type LegalFormQuestionProps = {
  onContinue: (payload: { businessForm: ExtendedBusinessForm }) => void;
};

interface LegalFormOption {
  label: string;
  description: string;
  value: ExtendedBusinessForm;
}

const legalFormOptions: LegalFormOption[] = [
  {
    description: locales.microButtonDescription,
    label: locales.microButtonTitle,
    value: 'Micro',
  },
  {
    description: locales.eiButtonDescription,
    label: locales.eiButtonTitle,
    value: 'EI',
  },
  {
    description: locales.gaecButtonDescription,
    label: locales.gaecButtonTitle,
    value: 'GAEC',
  },
  {
    description: locales.sarlButtonDescripton,
    label: locales.sarlButtonTitle,
    value: 'SARL',
  },
  {
    description: locales.sasButtonDescription,
    label: locales.sasButtonTitle,
    value: 'SAS',
  },
  {
    description: locales.sciButtonDescription,
    label: locales.sciButtonTitle,
    value: 'SCI',
  },
  {
    description: locales.scmButtonDescription,
    label: locales.scmButtonTitle,
    value: 'SCM',
  },
  {
    description: locales.sncButtonDescription,
    label: locales.sncButtonTitle,
    value: 'SNC',
  },
];

const legalFormQuestionSchema = z.object({
  businessForm: z.custom<ExtendedBusinessForm>((value) =>
    legalFormOptions.some((option) => option.value === value),
  ),
});

const LegalFormQuestion = asBentoPage<LegalFormQuestionProps>(
  ({ Layout, onContinue, onPrev }) => {
    const {
      control,
      formState: { isValid },
      handleSubmit,
    } = useForm({
      mode: 'onChange',
      resolver: zodResolver(legalFormQuestionSchema),
    });

    const onSubmit = handleSubmit((values) => {
      onContinue({ businessForm: values.businessForm });
    });

    return (
      <Layout
        data-testid="legal-form-question"
        header={<OBBackButton onClick={onPrev} />}
        nav={<OBNavigation onPrev={onPrev} />}
      >
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <Header title={locales.title} />

          <Flex
            as="form"
            direction="column"
            height="full"
            justify="space-between"
            onSubmit={onSubmit}
          >
            <Box>
              <Controller
                control={control}
                name="businessForm"
                render={({ field: { onChange, value } }) => (
                  <Form.Radio.Group
                    onChange={onChange}
                    radioPosition="right"
                    value={value ?? undefined}
                    variant="outline"
                  >
                    {legalFormOptions.map((option) => (
                      <Form.Radio key={option.value} value={option.value}>
                        <Form.Radio.Title>{option.label}</Form.Radio.Title>

                        <Form.Radio.Label>
                          {option.description}
                        </Form.Radio.Label>
                      </Form.Radio>
                    ))}
                  </Form.Radio.Group>
                )}
              />
            </Box>

            <OBStickyButton isDisabled={!isValid} type="submit">
              {locales.nextButtonLabel}
            </OBStickyButton>
          </Flex>
        </Flex>
      </Layout>
    );
  },
);

export default LegalFormQuestion;
